import React, { useState } from 'react';

export default function LandingPage() {
  const [showDemo, setShowDemo] = useState(false);
  const [formData, setFormData] = useState({
    name: '', email: '', company: '', phone: '',
    loadsPerMonth: '', currentTMS: '', preferredDate: '', preferredTime: '', message: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const coreFeatures = [
    { icon: '🚛', title: 'Load Management', desc: 'Create, track, and manage loads from pickup to delivery with real-time status updates.' },
    { icon: '📊', title: 'Rate Intelligence', desc: 'AI-powered rate recommendations based on lane history and market conditions.' },
    { icon: '🤝', title: 'Carrier Matching', desc: '100-point AI scoring system matches the best carriers to your loads automatically.' },
    { icon: '💵', title: 'Quick Pay & Factoring', desc: 'Integrated factoring with tiered fees. Carriers get paid fast, you maintain cash flow.' },
    { icon: '📍', title: 'GPS Tracking', desc: 'Real-time load tracking with automated check calls and ETA updates.' },
    { icon: '📄', title: 'Document Management', desc: 'Digital BOL, POD, and rate confirmations with e-signature compliance.' },
    { icon: '✅', title: 'FMCSA Compliance', desc: 'Automatic carrier verification, authority checks, and insurance validation.' },
    { icon: '🛢️', title: 'Oilfield Specialization', desc: 'Purpose-built for oilfield logistics with wellsite management and certifications.' },
    { icon: '💰', title: 'Invoicing & Settlement', desc: 'Auto-generate invoices, track AR/AP, and export to QuickBooks or any accounting system.' }
  ];

  const newFeatures = [
    { icon: '🧠', title: 'AI Copilot', desc: 'Ask questions in plain English. Get instant answers about your loads, carriers, and operations.', tag: 'NEW' },
    { icon: '🤖', title: 'Freight Agent', desc: 'Autonomous AI monitoring of your operations. Proactive alerts and recommendations 24/7.', tag: 'NEW' },
    { icon: '⛓️', title: 'Blockchain Ledger', desc: 'Tamper-proof BOLs and documents with SHA-256 hashing and digital signatures.', tag: 'NEW' },
    { icon: '🌱', title: 'Sustainability Dashboard', desc: 'Track CO₂ emissions, generate ESG reports, and showcase your environmental impact.', tag: 'NEW' },
    { icon: '💼', title: 'CRM & Sales', desc: 'Built-in CRM with contact management, chat sessions, and lead tracking.', tag: 'NEW' },
    { icon: '🔐', title: 'Enterprise Security', desc: 'Role-based access, audit logs, and advanced security settings for compliance.', tag: 'NEW' }
  ];

  const stats = [
    { value: '50%', label: 'Faster Dispatch' },
    { value: '30%', label: 'Cost Reduction' },
    { value: '99.9%', label: 'Uptime' },
    { value: '24/7', label: 'AI Monitoring' }
  ];

  const testimonials = [
    { name: 'Mike Johnson', role: 'Owner, J&M Logistics', quote: 'RoadOptix cut our dispatch time in half. The carrier matching alone pays for itself.' },
    { name: 'Sarah Chen', role: 'Operations Manager, FastFreight', quote: 'Finally, a TMS that understands oilfield hauling. The wellsite management is a game-changer.' },
    { name: 'David Williams', role: 'CFO, Regional Transport Co.', quote: 'The accounting exports saved us 20 hours a month. Seamless QuickBooks integration.' }
  ];

  async function handleSubmit(e) {
    e.preventDefault();
    try {
      await fetch('/api/demo-request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
    } catch (err) {
      console.log('Demo request:', formData);
    }
    setSubmitted(true);
  }

  return (
    <div style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" }}>
      {/* Navigation */}
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0,
        background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(10px)',
        padding: '16px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        zIndex: 1000, borderBottom: '1px solid #e5e7eb'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 28, fontWeight: 800, color: '#dc2626' }}>RoadOptix</span>
          <span style={{ fontSize: 12, background: '#fef3c7', color: '#92400e', padding: '2px 8px', borderRadius: 12, fontWeight: 600 }}>TMS</span>
        </div>
        <div style={{ display: 'flex', gap: 32, alignItems: 'center' }}>
          <a href="#features" style={{ color: '#374151', textDecoration: 'none', fontWeight: 500 }}>Features</a>
          <a href="#ai" style={{ color: '#374151', textDecoration: 'none', fontWeight: 500 }}>AI Platform</a>
          <a href="#pricing" style={{ color: '#374151', textDecoration: 'none', fontWeight: 500 }}>Pricing</a>
          <a href="#testimonials" style={{ color: '#374151', textDecoration: 'none', fontWeight: 500 }}>Testimonials</a>
          <button onClick={() => setShowDemo(true)} style={{ padding: '10px 24px', background: '#dc2626', color: 'white', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}>
            Schedule Demo
          </button>
          <a href="/login" style={{ padding: '10px 24px', background: '#374151', color: 'white', borderRadius: 8, fontWeight: 600, textDecoration: 'none' }}>
            Login
          </a>
        </div>
      </nav>

      {/* Hero Section */}
      <section style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'linear-gradient(135deg, #1f2937 0%, #111827 50%, #7f1d1d 100%)',
        padding: '120px 32px 80px', position: 'relative', overflow: 'hidden'
      }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', textAlign: 'center', position: 'relative', zIndex: 1 }}>
          {/* New Feature Badge */}
          <div style={{ 
            display: 'inline-flex', alignItems: 'center', gap: 8,
            background: 'linear-gradient(135deg, rgba(147,51,234,0.2), rgba(220,38,38,0.2))',
            padding: '8px 20px', borderRadius: 50, marginBottom: 24,
            border: '1px solid rgba(147,51,234,0.3)'
          }}>
            <span style={{ width: 8, height: 8, background: '#a855f7', borderRadius: '50%', animation: 'pulse 2s infinite' }}></span>
            <span style={{ color: '#c4b5fd', fontSize: 14, fontWeight: 500 }}>NEW: AI Copilot, Freight Agent & Blockchain Documents</span>
          </div>

          <h1 style={{ fontSize: 64, fontWeight: 800, color: 'white', margin: '0 0 24px', lineHeight: 1.1 }}>
            The AI-Powered TMS for<br />
            <span style={{ 
              background: 'linear-gradient(135deg, #dc2626, #f97316)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>Modern Freight Brokers</span>
          </h1>
          <p style={{ fontSize: 22, color: '#9ca3af', maxWidth: 750, margin: '0 auto 40px', lineHeight: 1.6 }}>
            Streamline dispatch, automate compliance, and scale your brokerage with AI-powered operations, 
            blockchain-secured documents, and integrated sustainability tracking.
          </p>
          <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={() => setShowDemo(true)} style={{ 
              padding: '16px 40px', background: 'linear-gradient(135deg, #dc2626, #b91c1c)', 
              color: 'white', border: 'none', borderRadius: 12, fontWeight: 700, fontSize: 18, cursor: 'pointer',
              boxShadow: '0 4px 20px rgba(220,38,38,0.4)'
            }}>
              Schedule Free Demo →
            </button>
            <button onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })} style={{ padding: '16px 40px', background: 'transparent', color: 'white', border: '2px solid #4b5563', borderRadius: 12, fontWeight: 600, fontSize: 18, cursor: 'pointer' }}>
              See Features
            </button>
          </div>
          
          {/* Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 32, marginTop: 80, padding: '32px 48px', background: 'rgba(255,255,255,0.05)', borderRadius: 16, border: '1px solid rgba(255,255,255,0.1)' }}>
            {stats.map((stat, i) => (
              <div key={i} style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 48, fontWeight: 800, color: '#dc2626' }}>{stat.value}</div>
                <div style={{ fontSize: 14, color: '#9ca3af', marginTop: 4 }}>{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Platform Section - NEW */}
      <section id="ai" style={{ padding: '100px 32px', background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 64 }}>
            <div style={{ 
              display: 'inline-flex', alignItems: 'center', gap: 8,
              background: 'rgba(168,85,247,0.2)', padding: '6px 16px', borderRadius: 50, marginBottom: 16
            }}>
              <span>🤖</span>
              <span style={{ color: '#c4b5fd', fontSize: 14, fontWeight: 600 }}>AI-POWERED PLATFORM</span>
            </div>
            <h2 style={{ fontSize: 42, fontWeight: 800, color: 'white', margin: '0 0 16px' }}>
              Your AI Operations Center
            </h2>
            <p style={{ fontSize: 18, color: '#a5b4fc', maxWidth: 600, margin: '0 auto' }}>
              Two powerful AI systems work together to optimize your freight operations 24/7
            </p>
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 32 }}>
            {/* AI Copilot */}
            <div style={{ background: 'rgba(255,255,255,0.1)', padding: 40, borderRadius: 20, border: '1px solid rgba(255,255,255,0.1)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
                <div style={{ 
                  width: 64, height: 64, borderRadius: 16,
                  background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32
                }}>🧠</div>
                <div>
                  <h3 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'white' }}>AI Copilot</h3>
                  <p style={{ margin: 0, color: '#a5b4fc', fontSize: 14 }}>Natural Language Interface</p>
                </div>
              </div>
              <p style={{ color: '#c7d2fe', marginBottom: 24, lineHeight: 1.7 }}>
                Ask questions in plain English. Get instant answers about your loads, carriers, rates, and operations.
              </p>
              <div style={{ background: 'rgba(0,0,0,0.3)', borderRadius: 12, padding: 16, marginBottom: 20 }}>
                <p style={{ color: '#9ca3af', fontSize: 13, marginBottom: 12 }}>Try asking:</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ background: 'rgba(139,92,246,0.3)', color: '#ddd6fe', padding: '8px 12px', borderRadius: 8, fontSize: 14 }}>
                    "Show me all loads going to Houston this week"
                  </div>
                  <div style={{ background: 'rgba(139,92,246,0.3)', color: '#ddd6fe', padding: '8px 12px', borderRadius: 8, fontSize: 14 }}>
                    "Which carriers have the best on-time rate?"
                  </div>
                  <div style={{ background: 'rgba(139,92,246,0.3)', color: '#ddd6fe', padding: '8px 12px', borderRadius: 8, fontSize: 14 }}>
                    "What's my profit margin on the Permian Basin lane?"
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                <span style={{ background: 'rgba(59,130,246,0.2)', color: '#93c5fd', padding: '4px 12px', borderRadius: 20, fontSize: 13 }}>✓ 6 Agentic Tools</span>
                <span style={{ background: 'rgba(59,130,246,0.2)', color: '#93c5fd', padding: '4px 12px', borderRadius: 20, fontSize: 13 }}>✓ Real-time Data</span>
                <span style={{ background: 'rgba(59,130,246,0.2)', color: '#93c5fd', padding: '4px 12px', borderRadius: 20, fontSize: 13 }}>✓ Claude AI</span>
              </div>
            </div>

            {/* Freight Agent */}
            <div style={{ background: 'rgba(255,255,255,0.1)', padding: 40, borderRadius: 20, border: '1px solid rgba(255,255,255,0.1)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
                <div style={{ 
                  width: 64, height: 64, borderRadius: 16,
                  background: 'linear-gradient(135deg, #10b981, #3b82f6)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32
                }}>🤖</div>
                <div>
                  <h3 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'white' }}>Freight Agent</h3>
                  <p style={{ margin: 0, color: '#6ee7b7', fontSize: 14 }}>Autonomous Monitoring</p>
                </div>
              </div>
              <p style={{ color: '#c7d2fe', marginBottom: 24, lineHeight: 1.7 }}>
                24/7 autonomous monitoring of your operations. Proactive alerts, carrier recommendations, and optimization suggestions.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
                <div style={{ background: 'rgba(0,0,0,0.3)', borderRadius: 12, padding: 16, textAlign: 'center' }}>
                  <div style={{ fontSize: 28, fontWeight: 800, color: 'white' }}>24/7</div>
                  <div style={{ fontSize: 12, color: '#9ca3af' }}>Monitoring</div>
                </div>
                <div style={{ background: 'rgba(0,0,0,0.3)', borderRadius: 12, padding: 16, textAlign: 'center' }}>
                  <div style={{ fontSize: 28, fontWeight: 800, color: 'white' }}>15min</div>
                  <div style={{ fontSize: 12, color: '#9ca3af' }}>Alert Cycle</div>
                </div>
                <div style={{ background: 'rgba(0,0,0,0.3)', borderRadius: 12, padding: 16, textAlign: 'center' }}>
                  <div style={{ fontSize: 28, fontWeight: 800, color: 'white' }}>Auto</div>
                  <div style={{ fontSize: 12, color: '#9ca3af' }}>Carrier Match</div>
                </div>
                <div style={{ background: 'rgba(0,0,0,0.3)', borderRadius: 12, padding: 16, textAlign: 'center' }}>
                  <div style={{ fontSize: 28, fontWeight: 800, color: 'white' }}>Smart</div>
                  <div style={{ fontSize: 12, color: '#9ca3af' }}>Recommendations</div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                <span style={{ background: 'rgba(16,185,129,0.2)', color: '#6ee7b7', padding: '4px 12px', borderRadius: 20, fontSize: 13 }}>✓ Delay Detection</span>
                <span style={{ background: 'rgba(16,185,129,0.2)', color: '#6ee7b7', padding: '4px 12px', borderRadius: 20, fontSize: 13 }}>✓ Rate Alerts</span>
                <span style={{ background: 'rgba(16,185,129,0.2)', color: '#6ee7b7', padding: '4px 12px', borderRadius: 20, fontSize: 13 }}>✓ Auto-Actions</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* New Features Section */}
      <section style={{ padding: '100px 32px', background: '#111827' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 64 }}>
            <div style={{ 
              display: 'inline-flex', alignItems: 'center', gap: 8,
              background: 'rgba(220,38,38,0.2)', padding: '6px 16px', borderRadius: 50, marginBottom: 16
            }}>
              <span>✨</span>
              <span style={{ color: '#fca5a5', fontSize: 14, fontWeight: 600 }}>NEW IN 2026</span>
            </div>
            <h2 style={{ fontSize: 42, fontWeight: 800, color: 'white', margin: '0 0 16px' }}>
              Powerful New Capabilities
            </h2>
            <p style={{ fontSize: 18, color: '#9ca3af', maxWidth: 600, margin: '0 auto' }}>
              Enterprise-grade features to take your brokerage to the next level
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24 }}>
            {newFeatures.map((feature, i) => (
              <div key={i} style={{ 
                background: 'linear-gradient(135deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02))', 
                padding: 32, borderRadius: 16, border: '1px solid rgba(255,255,255,0.1)',
                position: 'relative', overflow: 'hidden'
              }}>
                {feature.tag && (
                  <span style={{ 
                    position: 'absolute', top: 16, right: 16,
                    background: 'linear-gradient(135deg, #dc2626, #f97316)',
                    color: 'white', padding: '4px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700
                  }}>{feature.tag}</span>
                )}
                <div style={{ fontSize: 40, marginBottom: 16 }}>{feature.icon}</div>
                <h3 style={{ fontSize: 20, fontWeight: 700, color: 'white', margin: '0 0 12px' }}>{feature.title}</h3>
                <p style={{ fontSize: 15, color: '#9ca3af', margin: 0, lineHeight: 1.6 }}>{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Core Features Section */}
      <section id="features" style={{ padding: '100px 32px', background: '#f9fafb' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 64 }}>
            <h2 style={{ fontSize: 42, fontWeight: 800, color: '#111827', margin: '0 0 16px' }}>
              Everything You Need to Run a Modern Brokerage
            </h2>
            <p style={{ fontSize: 18, color: '#6b7280', maxWidth: 600, margin: '0 auto' }}>
              Built by freight professionals who understand your challenges.
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24 }}>
            {coreFeatures.map((feature, i) => (
              <div key={i} style={{ background: 'white', padding: 32, borderRadius: 16, border: '1px solid #e5e7eb', transition: 'all 0.2s' }}>
                <div style={{ fontSize: 40, marginBottom: 16 }}>{feature.icon}</div>
                <h3 style={{ fontSize: 20, fontWeight: 700, color: '#111827', margin: '0 0 12px' }}>{feature.title}</h3>
                <p style={{ fontSize: 15, color: '#6b7280', margin: 0, lineHeight: 1.6 }}>{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Blockchain & Security Section */}
      <section style={{ padding: '100px 32px', background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 64, alignItems: 'center' }}>
            <div>
              <div style={{ 
                display: 'inline-flex', alignItems: 'center', gap: 8,
                background: 'rgba(251,191,36,0.2)', padding: '6px 16px', borderRadius: 50, marginBottom: 16
              }}>
                <span>⛓️</span>
                <span style={{ color: '#fcd34d', fontSize: 14, fontWeight: 600 }}>BLOCKCHAIN SECURITY</span>
              </div>
              <h2 style={{ fontSize: 42, fontWeight: 800, color: 'white', margin: '0 0 24px', lineHeight: 1.2 }}>
                Tamper-Proof Documents.<br />
                <span style={{ color: '#fbbf24' }}>Complete Compliance.</span>
              </h2>
              <p style={{ fontSize: 18, color: '#94a3b8', marginBottom: 32, lineHeight: 1.7 }}>
                Every BOL, rate confirmation, and POD is secured with blockchain technology. 
                SHA-256 hashing ensures document integrity. Digital signatures provide non-repudiation.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div style={{ background: 'rgba(255,255,255,0.05)', padding: 20, borderRadius: 12 }}>
                  <div style={{ fontSize: 24, marginBottom: 8 }}>📄</div>
                  <div style={{ color: 'white', fontWeight: 600 }}>Digital BOLs</div>
                  <div style={{ color: '#64748b', fontSize: 13 }}>Auto-generated & secured</div>
                </div>
                <div style={{ background: 'rgba(255,255,255,0.05)', padding: 20, borderRadius: 12 }}>
                  <div style={{ fontSize: 24, marginBottom: 8 }}>✍️</div>
                  <div style={{ color: 'white', fontWeight: 600 }}>E-Signatures</div>
                  <div style={{ color: '#64748b', fontSize: 13 }}>Cryptographic verification</div>
                </div>
                <div style={{ background: 'rgba(255,255,255,0.05)', padding: 20, borderRadius: 12 }}>
                  <div style={{ fontSize: 24, marginBottom: 8 }}>🔍</div>
                  <div style={{ color: 'white', fontWeight: 600 }}>Audit Trail</div>
                  <div style={{ color: '#64748b', fontSize: 13 }}>Every action logged</div>
                </div>
                <div style={{ background: 'rgba(255,255,255,0.05)', padding: 20, borderRadius: 12 }}>
                  <div style={{ fontSize: 24, marginBottom: 8 }}>🔐</div>
                  <div style={{ color: 'white', fontWeight: 600 }}>Role-Based Access</div>
                  <div style={{ color: '#64748b', fontSize: 13 }}>Enterprise security</div>
                </div>
              </div>
            </div>
            <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 20, padding: 32, border: '1px solid rgba(255,255,255,0.1)' }}>
              <h3 style={{ color: 'white', fontSize: 20, marginBottom: 24 }}>Document Verification</h3>
              <div style={{ background: '#0f172a', borderRadius: 12, padding: 20, marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                  <span style={{ color: '#94a3b8' }}>Document</span>
                  <span style={{ color: 'white' }}>BOL-2026-00847</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                  <span style={{ color: '#94a3b8' }}>Hash</span>
                  <span style={{ color: '#22c55e', fontFamily: 'monospace', fontSize: 12 }}>a7b9c3d2...</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                  <span style={{ color: '#94a3b8' }}>Signatures</span>
                  <span style={{ color: 'white' }}>3 verified</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#94a3b8' }}>Status</span>
                  <span style={{ color: '#22c55e', fontWeight: 600 }}>✓ Verified</span>
                </div>
              </div>
              <div style={{ background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 12, padding: 16, textAlign: 'center' }}>
                <span style={{ color: '#22c55e', fontWeight: 600 }}>⛓️ Chain Integrity: Valid</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sustainability Section */}
      <section style={{ padding: '100px 32px', background: 'linear-gradient(135deg, #064e3b 0%, #065f46 100%)' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 64, alignItems: 'center' }}>
            <div style={{ background: 'white', borderRadius: 20, padding: 32 }}>
              <h3 style={{ fontSize: 20, fontWeight: 700, color: '#111827', marginBottom: 24 }}>Environmental Impact Dashboard</h3>
              <div style={{ display: 'grid', gap: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: 16, background: '#f0fdf4', borderRadius: 12 }}>
                  <span style={{ color: '#166534' }}>🌳 Trees Equivalent</span>
                  <span style={{ fontWeight: 700, color: '#166534' }}>12,847</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: 16, background: '#eff6ff', borderRadius: 12 }}>
                  <span style={{ color: '#1d4ed8' }}>🚗 Cars Off Road</span>
                  <span style={{ fontWeight: 700, color: '#1d4ed8' }}>45.2/year</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: 16, background: '#fefce8', borderRadius: 12 }}>
                  <span style={{ color: '#a16207' }}>⛽ Gallons Saved</span>
                  <span style={{ fontWeight: 700, color: '#a16207' }}>8,234</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: 20, background: '#dcfce7', borderRadius: 12, marginTop: 8 }}>
                  <span style={{ color: '#166534', fontWeight: 600 }}>Total CO₂ Saved</span>
                  <span style={{ fontWeight: 800, color: '#166534', fontSize: 24 }}>269,787 kg</span>
                </div>
              </div>
            </div>
            <div>
              <div style={{ 
                display: 'inline-flex', alignItems: 'center', gap: 8,
                background: 'rgba(34,197,94,0.2)', padding: '6px 16px', borderRadius: 50, marginBottom: 16
              }}>
                <span>🌱</span>
                <span style={{ color: '#86efac', fontSize: 14, fontWeight: 600 }}>SUSTAINABILITY</span>
              </div>
              <h2 style={{ fontSize: 42, fontWeight: 800, color: 'white', margin: '0 0 24px', lineHeight: 1.2 }}>
                Track Your<br />
                <span style={{ color: '#86efac' }}>Environmental Impact</span>
              </h2>
              <p style={{ fontSize: 18, color: '#a7f3d0', marginBottom: 32, lineHeight: 1.7 }}>
                Automatic carbon footprint tracking for every load. Generate ESG reports with one click. 
                Show customers your commitment to sustainability.
              </p>
              <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontSize: 32, fontWeight: 800, color: 'white' }}>1-Click</div>
                  <div style={{ color: '#6ee7b7', fontSize: 14 }}>ESG Reports</div>
                </div>
                <div>
                  <div style={{ fontSize: 32, fontWeight: 800, color: 'white' }}>EPA</div>
                  <div style={{ color: '#6ee7b7', fontSize: 14 }}>SmartWay Math</div>
                </div>
                <div>
                  <div style={{ fontSize: 32, fontWeight: 800, color: 'white' }}>Real-Time</div>
                  <div style={{ color: '#6ee7b7', fontSize: 14 }}>Tracking</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" style={{ padding: '100px 32px', background: 'white' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 64 }}>
            <h2 style={{ fontSize: 42, fontWeight: 800, color: '#111827', margin: '0 0 16px' }}>Simple, Transparent Pricing</h2>
            <p style={{ fontSize: 18, color: '#6b7280' }}>No hidden fees. No long-term contracts. Scale as you grow.</p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24 }}>
            {/* Starter */}
            <div style={{ background: '#f9fafb', padding: 32, borderRadius: 16, border: '1px solid #e5e7eb' }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#6b7280', marginBottom: 8 }}>STARTER</div>
              <div style={{ fontSize: 48, fontWeight: 800, color: '#111827' }}>$199<span style={{ fontSize: 18, fontWeight: 500, color: '#6b7280' }}>/mo</span></div>
              <div style={{ fontSize: 14, color: '#6b7280', marginBottom: 24 }}>Up to 100 loads/month</div>
              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 24px' }}>
                {['Load management', 'Carrier database', 'Basic reporting', 'Document storage', 'Email support'].map((item, i) => (
                  <li key={i} style={{ padding: '8px 0', color: '#374151' }}>✓ {item}</li>
                ))}
              </ul>
              <button onClick={() => setShowDemo(true)} style={{ width: '100%', padding: '12px 24px', background: '#374151', color: 'white', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}>Get Started</button>
            </div>
            {/* Professional */}
            <div style={{ background: '#111827', padding: 32, borderRadius: 16, position: 'relative', transform: 'scale(1.05)' }}>
              <div style={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)', background: 'linear-gradient(135deg, #dc2626, #f97316)', color: 'white', padding: '4px 16px', borderRadius: 20, fontSize: 12, fontWeight: 700 }}>MOST POPULAR</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#9ca3af', marginBottom: 8 }}>PROFESSIONAL</div>
              <div style={{ fontSize: 48, fontWeight: 800, color: 'white' }}>$499<span style={{ fontSize: 18, fontWeight: 500, color: '#9ca3af' }}>/mo</span></div>
              <div style={{ fontSize: 14, color: '#9ca3af', marginBottom: 24 }}>Up to 500 loads/month</div>
              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 24px' }}>
                {['Everything in Starter', 'AI Copilot', 'Freight Agent', 'GPS tracking', 'Quick Pay & Factoring', 'Blockchain documents', 'Sustainability tracking', 'Priority support'].map((item, i) => (
                  <li key={i} style={{ padding: '8px 0', color: '#e5e7eb' }}>✓ {item}</li>
                ))}
              </ul>
              <button onClick={() => setShowDemo(true)} style={{ width: '100%', padding: '12px 24px', background: 'linear-gradient(135deg, #dc2626, #b91c1c)', color: 'white', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}>Get Started</button>
            </div>
            {/* Enterprise */}
            <div style={{ background: '#f9fafb', padding: 32, borderRadius: 16, border: '1px solid #e5e7eb' }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#6b7280', marginBottom: 8 }}>ENTERPRISE</div>
              <div style={{ fontSize: 48, fontWeight: 800, color: '#111827' }}>Custom</div>
              <div style={{ fontSize: 14, color: '#6b7280', marginBottom: 24 }}>Unlimited loads</div>
              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 24px' }}>
                {['Everything in Professional', 'SAP integration', 'Multi-broker support', 'API access', 'Custom integrations', 'Dedicated account manager', 'SLA guarantee'].map((item, i) => (
                  <li key={i} style={{ padding: '8px 0', color: '#374151' }}>✓ {item}</li>
                ))}
              </ul>
              <button onClick={() => setShowDemo(true)} style={{ width: '100%', padding: '12px 24px', background: '#374151', color: 'white', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}>Contact Sales</button>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" style={{ padding: '100px 32px', background: '#f9fafb' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 64 }}>
            <h2 style={{ fontSize: 42, fontWeight: 800, color: '#111827', margin: '0 0 16px' }}>Trusted by Growing Brokerages</h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24 }}>
            {testimonials.map((t, i) => (
              <div key={i} style={{ background: 'white', padding: 32, borderRadius: 16, border: '1px solid #e5e7eb' }}>
                <div style={{ fontSize: 40, color: '#dc2626', marginBottom: 16 }}>"</div>
                <p style={{ fontSize: 16, color: '#374151', lineHeight: 1.7, margin: '0 0 24px' }}>{t.quote}</p>
                <div>
                  <div style={{ fontWeight: 700, color: '#111827' }}>{t.name}</div>
                  <div style={{ fontSize: 14, color: '#6b7280' }}>{t.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section style={{ padding: '100px 32px', background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)' }}>
        <div style={{ maxWidth: 800, margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{ fontSize: 42, fontWeight: 800, color: 'white', margin: '0 0 16px' }}>Ready to Modernize Your Brokerage?</h2>
          <p style={{ fontSize: 18, color: 'rgba(255,255,255,0.9)', marginBottom: 32 }}>Join hundreds of brokers who've streamlined their operations with RoadOptix.</p>
          <button onClick={() => setShowDemo(true)} style={{ padding: '16px 48px', background: 'white', color: '#dc2626', border: 'none', borderRadius: 12, fontWeight: 700, fontSize: 18, cursor: 'pointer' }}>
            Schedule Your Free Demo
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ padding: '48px 32px', background: '#111827' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 24 }}>
          <div>
            <div style={{ fontSize: 24, fontWeight: 800, color: 'white', marginBottom: 8 }}>RoadOptix</div>
            <div style={{ color: '#6b7280', fontSize: 14 }}>© 2026 Symple Code Business Systems. All rights reserved.</div>
          </div>
          <div style={{ display: 'flex', gap: 32 }}>
            <a href="/privacy" style={{ color: '#9ca3af', textDecoration: 'none', fontSize: 14 }}>Privacy Policy</a>
            <a href="/terms" style={{ color: '#9ca3af', textDecoration: 'none', fontSize: 14 }}>Terms of Service</a>
            <a href="/sms-consent" style={{ color: '#9ca3af', textDecoration: 'none', fontSize: 14 }}>SMS Terms</a>
            <a href="mailto:sales@roadoptix.com" style={{ color: '#9ca3af', textDecoration: 'none', fontSize: 14 }}>Contact</a>
          </div>
        </div>
      </footer>

      {/* Demo Modal */}
      {showDemo && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 20 }}>
          <div style={{ background: 'white', borderRadius: 16, padding: 40, maxWidth: 500, width: '100%', maxHeight: '90vh', overflow: 'auto' }}>
            {!submitted ? (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                  <h2 style={{ margin: 0, fontSize: 24 }}>Schedule a Demo</h2>
                  <button onClick={() => setShowDemo(false)} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#6b7280' }}>×</button>
                </div>
                <p style={{ color: '#6b7280', marginBottom: 24 }}>See RoadOptix in action. Our team will walk you through the AI-powered platform.</p>
                <form onSubmit={handleSubmit}>
                  <div style={{ display: 'grid', gap: 16 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                      <div>
                        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Full Name *</label>
                        <input type="text" required value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid #d1d5db', fontSize: 15, boxSizing: 'border-box' }} />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Company *</label>
                        <input type="text" required value={formData.company} onChange={e => setFormData({ ...formData, company: e.target.value })} style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid #d1d5db', fontSize: 15, boxSizing: 'border-box' }} />
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                      <div>
                        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Email *</label>
                        <input type="email" required value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid #d1d5db', fontSize: 15, boxSizing: 'border-box' }} />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Phone</label>
                        <input type="tel" value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid #d1d5db', fontSize: 15, boxSizing: 'border-box' }} />
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                      <div>
                        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Preferred Date *</label>
                        <input type="date" required value={formData.preferredDate} onChange={e => setFormData({ ...formData, preferredDate: e.target.value })} min={new Date().toISOString().split('T')[0]} style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid #d1d5db', fontSize: 15, boxSizing: 'border-box' }} />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Preferred Time *</label>
                        <select required value={formData.preferredTime} onChange={e => setFormData({ ...formData, preferredTime: e.target.value })} style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid #d1d5db', fontSize: 15, boxSizing: 'border-box' }}>
                          <option value="">Select...</option>
                          <option value="9:00 AM CST">9:00 AM CST</option>
                          <option value="10:00 AM CST">10:00 AM CST</option>
                          <option value="11:00 AM CST">11:00 AM CST</option>
                          <option value="1:00 PM CST">1:00 PM CST</option>
                          <option value="2:00 PM CST">2:00 PM CST</option>
                          <option value="3:00 PM CST">3:00 PM CST</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Message (optional)</label>
                      <textarea rows={3} value={formData.message} onChange={e => setFormData({ ...formData, message: e.target.value })} placeholder="Anything specific you'd like to see?" style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid #d1d5db', fontSize: 15, resize: 'vertical', boxSizing: 'border-box' }} />
                    </div>
                    <button type="submit" style={{ width: '100%', padding: '14px 24px', background: 'linear-gradient(135deg, #dc2626, #b91c1c)', color: 'white', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 16, cursor: 'pointer', marginTop: 8 }}>
                      Request Demo
                    </button>
                  </div>
                </form>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <div style={{ fontSize: 64, marginBottom: 16 }}>🎉</div>
                <h2 style={{ margin: '0 0 16px' }}>Demo Requested!</h2>
                <p style={{ color: '#6b7280', marginBottom: 24 }}>Thanks! We'll send a calendar invite to <strong>{formData.email}</strong> shortly.</p>
                <button onClick={() => { setShowDemo(false); setSubmitted(false); setFormData({ name: '', email: '', company: '', phone: '', loadsPerMonth: '', currentTMS: '', preferredDate: '', preferredTime: '', message: '' }); }} style={{ padding: '12px 32px', background: '#374151', color: 'white', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}>
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
